import { getCurrentSettings } from './settings.js';
import { renderFullMelodySequence, renderMelodyBlocks, updateFullMelodySequenceBlocks } from './guitar.js';
import { getNoteNameForPosition, noteNameToFrequency } from './utils.js';
import { getGameMode, getSequence, getSpeedFactor } from "./game.js";
import { resetCurrentNoteId, setListening } from "./tuner.js";
import { fetchSongData, fetchSongParts } from "./api";
import { getUserConfig } from "./userconfig";
import { threeJSBand } from './threejsBand.js';
import { translate } from './translation.js';
import * as Tone from 'tone';

class NotesManager {
    constructor() {
        this.notesSequence = [];
        this.currentNoteIndex = -1;
        this.audioSources = [];
        this.currentTempo = 120; // Значение по умолчанию
        this.melodyBlocks = [];
        this.fullMelodySequence = [];

        this.COLOR_PALETTE = [
            '#E040FB', '#536DFE', '#4CAF50',
            '#E91E63', '#F44336', '#FFC107',
        ];

        // Элементы DOM (могут отсутствовать, поэтому далее в коде везде делаем проверки)
        this.currentNoteDisplay = document.getElementById('currentNoteDisplay');
        this.progressDisplay = document.getElementById('progressDisplay');

        //this.initNotes();
    }

    // Инициализация нот
    async init() {
        try {
            const userConfig = await getUserConfig();
            const userProgress = userConfig.userProgress || [];
            const songId = userProgress[0]?.song_id || 1;

            const songData = await fetchSongData(songId);
            const songParts = await fetchSongParts(songId);

            this.createNotesSequence(songData, songParts);
            await renderMelodyBlocks();
            renderFullMelodySequence();
            // Обновляем состояние блоков после рендеринга (чтобы учесть прогресс)
            updateFullMelodySequenceBlocks();
        } catch (error) {
            console.error('Ошибка загрузки данных:', error);
            throw error;
        }
    }

    getUniqueColor(usedColors, blockId) {
        // Используем хэш от ID блока для детерминированного выбора цвета
        const colorIndex = (blockId * 13) % this.COLOR_PALETTE.length; // Простое число для лучшего распределения

        let selectedColor = this.COLOR_PALETTE[colorIndex];
        let attempts = 0;

        // Если цвет уже использован, ищем следующий доступный
        while (usedColors.has(selectedColor) && attempts < this.COLOR_PALETTE.length) {
            const nextIndex = (colorIndex + attempts + 1) % this.COLOR_PALETTE.length;
            selectedColor = this.COLOR_PALETTE[nextIndex];
            attempts++;
        }

        usedColors.add(selectedColor);
        return selectedColor;
    }

    // Проверка, является ли текущий элемент аккордом
    isCurrentChord() {
        const current = getCurrentNote();
        return current && current.length > 1;
    }

    // Переход к следующей ноте/аккорду
    // Обновляем метод nextNote для передачи следующей ноты
    async nextNote() {
        const currentSequence = getSequence();

        if (!currentSequence || !Array.isArray(currentSequence)) {
            console.error("Invalid sequence:", currentSequence);
            if (this.currentNoteDisplay) {
                this.currentNoteDisplay.textContent = translate("Ошибка: последовательность не загружена");
            }
            return;
        }

        if (this.currentNoteIndex < currentSequence.length - 1) {
            this.currentNoteIndex++;
            updateProgressDisplay();

            // Сбрасываем флаг currentNoteId для новой ноты
            resetCurrentNoteId();
        } else {
            // Завершение последовательности – если есть DOM-элемент, показываем сообщение,
            // иначе просто логируем, чтобы не ломать поток
            if (this.currentNoteDisplay) {
                this.currentNoteDisplay.textContent = translate("Последовательность завершена!");
            } else {
                console.info(translate("Последовательность завершена!"));
            }
        }

        // Гарантируем, что тюнер готов слушать следующую ноту
        setListening(true);

        const note = getCurrentNote();
        const nextNote = this.getNextNote();

        const isGamingMode = typeof getGameMode === 'function' ? getGameMode() : false;
        const isStaticGameMode = isGamingMode && getSpeedFactor && getSpeedFactor() === 0;

        if (note && threeJSBand && !isGamingMode && !isStaticGameMode) {
            threeJSBand.displayNote(note, nextNote, Tone.now() + 0.1);
        }
    }

    // Обновление отображения прогресса
    updateProgressDisplay() {
        if (!getGameMode) return;

        // Если элементы UI отсутствуют (например, панель последовательности скрыта) – просто выходим,
        // чтобы логика игры и прослушивания продолжала работать без ошибок.
        if (!this.progressDisplay || !this.currentNoteDisplay) {
            return;
        }

        let currentSequence = getSequence();
        this.progressDisplay.textContent = `${this.currentNoteIndex + 1}/${currentSequence.length}`;
        const currentNote = getCurrentNote();

        if (currentNote) {
            if (isCurrentChord()) {
                const chordNotes = currentNote.map(n => n.noteName).join(' + ');
                this.currentNoteDisplay.textContent = `${translate('Аккорд:')} ${chordNotes}`;
            } else {
                // Для одиночных нот currentNote - это массив с одним элементом
                this.currentNoteDisplay.textContent = currentNote[0].noteName;
            }
        }
    }

    // Остановка всех звуков
    stopAllAudio() {
        this.audioSources.forEach(source => {
            try {
                source.stop();
                source.disconnect();
            } catch (e) {
                console.warn('Ошибка при остановке аудио:', e);
            }
        });
        this.audioSources = [];
    }

    // Создание последовательности с поддержкой аккордов
    createNotesSequence(songData, songParts) {
        const settings = getCurrentSettings();
        const stringNotes = settings.stringNotes;
        this.currentTempo = songData.tempo || 120;

        let measures = [];
        const minGameLevelsByBlock = {};

        // Обрабатываем ноты и паузы
        songParts.forEach(part => {
            let measure = JSON.parse(part.sequence);
            measure.forEach(chordGroup => {
                // Обрабатываем ноты
                chordGroup.forEach(noteData => {
                    noteData.forEach(notes => {
                        if (notes.pause === undefined) {
                            const noteName = getNoteNameForPosition(
                                notes.string,
                                notes.fret,
                                stringNotes
                            );
                            const frequency = noteNameToFrequency(noteName);
                            notes.noteName = noteName;
                            notes.frequency = frequency;
                        }
                    });
                });
            });
            measures[part.num_sequence] = measure;

            // Сохраняем минимальный игровой уровень для блока
            if (typeof part.min_game_level === 'number') {
                minGameLevelsByBlock[part.num_sequence] = part.min_game_level;
            } else {
                minGameLevelsByBlock[part.num_sequence] = Number(part.min_game_level || 0);
            }
        });

        this.notesSequence = measures;
        this.minGameLevelsByBlock = minGameLevelsByBlock;
        this.currentNoteIndex = -1;

        this.createMelodyBlocks();
        this.createFullMelodySequence(songData.sequence_order);
    }

    // Создаем блоки мелодии на основе повторяющихся тактов
    createMelodyBlocks() {
        let measures = this.notesSequence;
        this.melodyBlocks = [];
        const usedColors = new Set(); // Множество для отслеживания использованных цветов

        // Группируем повторяющиеся такты в блоки
        measures.forEach((measure, id) => {
            // Получаем уникальный цвет для блока
            const blockColor = this.getUniqueColor(usedColors, id);

            // Создаем новый блок
            const currentBlock = {
                id: id,
                measures: measure,
                color: blockColor,
                minGameLevel: this.minGameLevelsByBlock?.[id] ?? 0
            };

            this.melodyBlocks.push(currentBlock);
        });

        console.log(`Создано ${this.melodyBlocks.length} блоков с уникальными цветами`);
    }

    // Получение блоков мелодии
    getMelodyBlocks() {
        return this.melodyBlocks;
    }

    // Получение мер для блока
    getMeasuresForBlock(blockId) {
        const block = this.melodyBlocks.find(b => b.id === blockId);
        return block ? block.measures : [];
    }

    getBlockMinGameLevel(blockId) {
        const block = this.melodyBlocks.find(b => b.id === blockId);
        if (!block) return 0;
        return Number(block.minGameLevel || 0);
    }

    getCurrentTempo() {
        return this.currentTempo;
    }

    setCurrentNoteIndex(index) {
        this.currentNoteIndex = index;
    }

    getCurrentNoteIndex() {
        return this.currentNoteIndex;
    }

    getCurrentNote() {
        const currentSequence = getSequence();

        // Добавить проверки
        if (!currentSequence || !Array.isArray(currentSequence)) return null;
        if (this.currentNoteIndex < 0 || this.currentNoteIndex >= currentSequence.length) return null;

        return currentSequence[this.currentNoteIndex];
    }

    getSongData() {
        return this.notesSequence;
    }

    findBlockByMeasureId(id) {
        // Заменяем эту проблемную строку:
        // return this.melodyBlocks.find(b => b.measureIds.includes(id));

        // На эту корректную реализацию:
        const block = this.melodyBlocks.find(b => b.id === id);
        if (block) {
            return block;
        }

        // Альтернативно: ищем блок, содержащий меру с указанным ID
        return this.melodyBlocks.find(block =>
            block.measures &&
            block.measures.some(measure =>
                    measure && measure.some(chordGroup =>
                            chordGroup && chordGroup.some(noteData =>
                                noteData && noteData.some(note => note.measureId === id)
                            )
                    )
            )
        );
    }

    // Создание полной последовательности мелодии
    createFullMelodySequence(sequenceOrder) {
        this.fullMelodySequence = [];

        // Парсим sequence_order (формат: "{1:10,2:1,1:12,3:1,4:1,6:1,1:4,0:7,5:1}")
        const orderStr = sequenceOrder.replace(/[{}]/g, '');
        const pairs = orderStr.split(',');

        pairs.forEach(pair => {
            const [blockId, repetitions] = pair.split(':').map(Number);

            // Обрабатываем паузы (blockId = 0)
            if (blockId === 0) {
                for (let i = 0; i < repetitions; i++) {
                    this.fullMelodySequence.push({
                        id: 0, // ID для паузы
                        color: 'transparent',
                        isPause: true,
                        sequenceIndex: this.fullMelodySequence.length,
                        repetitions: repetitions
                    });
                }
            } else {
                // Находим блок по ID
                const block = this.melodyBlocks.find(b => b.id === blockId);
                if (block) {
                    for (let i = 0; i < repetitions; i++) {
                        this.fullMelodySequence.push({
                            id: block.id,
                            color: block.color,
                            sequenceIndex: this.fullMelodySequence.length,
                            isPause: false
                        });
                    }
                }
            }
        });
    }

    // Получение полной последовательности
    getFullMelodySequence() {
        return this.fullMelodySequence;
    }

    // В класс NotesManager добавляем:
    getNextNote() {
        const currentSequence = getSequence();

        if (!currentSequence || !Array.isArray(currentSequence)) return null;
        if (this.currentNoteIndex < 0 || this.currentNoteIndex >= currentSequence.length - 1) return null;

        return currentSequence[this.currentNoteIndex + 1];
    }

    // Получить следующую ноту без переключения индекса (для проверки пауз)
    peekNextNote() {
        const currentSequence = getSequence();

        if (!currentSequence || !Array.isArray(currentSequence)) return null;
        if (this.currentNoteIndex < 0 || this.currentNoteIndex >= currentSequence.length - 1) return null;

        return currentSequence[this.currentNoteIndex + 1];
    }
}

// Создаем и экспортируем экземпляр
const notesManager = new NotesManager();
/** Загружает данные мелодии и рендерит UI блоков/последовательности */
export const initNotes = async () => {
    await notesManager.init();
};

/** Возвращает true, если текущая цель — аккорд */
export const isCurrentChord = () => notesManager.isCurrentChord();
/** Переходит к следующей ноте/аккорду и обновляет визуализацию */
export const nextNote = async () => await notesManager.nextNote();
/** Обновляет числовой прогресс (x/y) */
export const updateProgressDisplay = () => notesManager.updateProgressDisplay();
/** Останавливает все активные аудио-источники нот */
export const stopAllAudio = () => notesManager.stopAllAudio();
/** Возвращает список блоков мелодии */
export const getMelodyBlocks = () => notesManager.getMelodyBlocks();
/** Возвращает меры и группы нот для блока */
export const getMeasuresForBlock = (blockId) => notesManager.getMeasuresForBlock(blockId);
/** Минимальный игровой уровень для блока (из song_parts.min_game_level) */
export const getBlockMinGameLevel = (blockId) => notesManager.getBlockMinGameLevel(blockId);
/** Текущий темп мелодии (BPM) */
export const getCurrentTempo = () => notesManager.getCurrentTempo();
/** Устанавливает индекс текущей ноты в последовательности */
export const setCurrentNoteIndex = (index) => notesManager.setCurrentNoteIndex(index);
/** Возвращает индекс текущей ноты */
export const getCurrentNoteIndex = () => notesManager.getCurrentNoteIndex();
/** Возвращает текущую ноту/аккорд */
export const getCurrentNote = () => notesManager.getCurrentNote();
/** Возвращает исходные данные песни (метаданные/структура) */
export const getSongData = () => notesManager.getSongData();
/** Находит блок по id меры */
export const findBlockByMeasureId = (id) => notesManager.findBlockByMeasureId(id);
/** Возвращает полную последовательность блоков песни */
export const getFullMelodySequence = () => notesManager.getFullMelodySequence();